import _ from "lodash";
import React, { useState, useCallback } from "react";
import I18nHOC from "UIComponents/I18nHOC";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import withLoader from "UIComponents/GqlWithLoader";
import PropTypes from "prop-types";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import TagPractices from "./TagPractices";
import EvaluationCycles from "./EvaluationCycles";
import { getSnPEvaluationCyclesQuery } from "SnP_v2/modules/SnPQueries";
import { getSnPEvaluationCyclesFromCache } from "SnP_v2/modules/SnPGraphqlHelpers";
import { getSnpV2Cycles } from "SnP_v2/modules/SnPUtils";
import { tagPractices } from "SnP_v2/modules/SnPModule";
import classes from "./TagSnp.scss";
import { CloseOutlined } from "@toddle-edu/ds-icons";
import { IconButton } from "@toddle-edu/ds-web";
import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
import {
  ENABLE_GLOBAL_HEADER_ON_MULTIPLE_SCREENS,
  SNPv2_MANAGE_EVALUATION_CYCLES_PERMISSION,
  SNPv2_VISITOR_PORTAL_PERM_GROUP,
} from "Constants/permissionConstants";
import ACLStore from "lib/aclStore";
import ACLStorage from "lib/aclService";

import {
  getCycleIdToSetIdMappingMemoized,
  getPracticesForCycleMemoized,
  getAddedPracticesMemoized,
  getRemovedPracticesMemoized,
  getCycleIdToSelectedPracticesCountMappingMemoized,
  getApprovedPracticesForCycle,
  getPracticeWrappersForCycle,
} from "./TagSnpUtils";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

/**
 * @params
 *  cycles - list of evaluation cycles corresponding to current curriculum program
 *  tagPracticeMutation - mutation handler for tagging practice with evidence(item)
 *  itemId - id of evidence
 *  closeTagSnP - To close Parent Container of TagSnP
 *  updateTaggedPractices - Function for updating Tagged Practice State outside TagSnP
 *  taggedPractices - data structure having info of previously selected practices
 */

/**data structure info: */
/**taggedPractices -
 * [{evaluationCycle-Object,
 *   taggedPractice:[{
 *                      taggedBy:{id},
 *                      practice:{id},
 *                      evidenceStatus:String}]
 * }] */

const TagSnp = props => {
  const {
    cycles,
    t,
    tagPractices,
    itemId,
    itemType,
    itemGroupType,
    itemTitle,
    thumbnailData,
    closeModal,
    taggedPractices,
    steps,
    parentCycleId,
    parentSetId,
    userId,
    showApprovedPractices,
    assignmentFeedFilters,
    canManageEvaluationCycles,
    isVisitor,
    showDesignV2,
    isGlobalHeaderOnMultipleScreensEnabled,
    testId,
  } = props;

  const [currentCycle, setCurrentCycle] = useState(parentCycleId);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const cycleIdToSetIdMapping = getCycleIdToSetIdMappingMemoized({
    cycles,
  });

  const goToNextPage = () => {
    setCurrentStepIndex(currentStepIndex => currentStepIndex + 1);
  };

  const goToPreviousPage = () => {
    setCurrentStepIndex(currentStepIndex => currentStepIndex - 1);
  };

  const updateCurrentCycle = useCallback(({ id }) => {
    setCurrentCycle(id);
    goToNextPage();
  }, []);

  const practiceWrappers = getPracticeWrappersForCycle({
    taggedPractices,
    cycleId: currentCycle,
  });

  const {
    taggedPracticesForCycle,
    disabledPracticesForCycle,
  } = getPracticesForCycleMemoized({
    practiceWrappers,
    userId,
    canManageEvaluationCycles,
  });

  const approvedPractices = showApprovedPractices
    ? getApprovedPracticesForCycle({
        practiceWrappers,
      })
    : [];

  const cycleIdToTaggedPracticesCountMapping = getCycleIdToSelectedPracticesCountMappingMemoized(
    { cycles, taggedPractices }
  );

  const updateTaggedPractices = async ({ selectedPractices }) => {
    setIsLoading(true);

    try {
      await tagPractices({
        evaluationCycleId: currentCycle,
        addedPractices: getAddedPracticesMemoized({
          selectedPractices,
          taggedPractices: taggedPracticesForCycle,
        }),

        removedPractices: getRemovedPracticesMemoized({
          selectedPractices,
          taggedPractices: taggedPracticesForCycle,
        }),
        itemId,
        itemType: itemGroupType ? itemGroupType : itemType,
        assignmentFeedFilters,
        canManageEvaluationCycles,
        isVisitor,
      });
      setIsLoading(false);
      closeModal();
    } catch (e) {
      setIsLoading(false);
    }
  };

  const handleTaggedPractices = data => {
    if (props.updateTaggedPractices) {
      const selectedPractices = _.get(data, "selectedPractices", []);

      const addedPractices = getAddedPracticesMemoized({
        selectedPractices,
        taggedPractices: taggedPracticesForCycle,
      });

      const removedPractices = getRemovedPracticesMemoized({
        selectedPractices,
        taggedPractices: taggedPracticesForCycle,
      });

      props.updateTaggedPractices({
        evaluationCycle: { id: currentCycle },
        addedPractices,
        removedPractices,
      });
      closeModal();
    } else updateTaggedPractices(data);
  };

  const closeButtonElement = (
    <IconButton
      onClick={closeModal}
      testId={`${testId}-tagSnp-close`}
      icon={<CloseOutlined />}
      variant={"plain"}
      size={showDesignV2 ? "medium" : "large"}
    />
  );

  const currentCycleDetails = _.find(cycles, ["id", currentCycle]) ?? "";

  const { title: cycleName = "", cycleType = "" } = currentCycleDetails;

  const practiceListsCollection = {
    approvedPractices,
    taggedPractices: taggedPracticesForCycle,
    disabledPractices: disabledPracticesForCycle,
  };

  const getCurrentScreen = () => {
    const currentStep = steps[currentStepIndex];
    switch (currentStep) {
      case "CYCLE_LIST": {
        return (
          <EvaluationCycles
            updateCurrentCycle={updateCurrentCycle}
            cycles={cycles}
            closeButtonElement={closeButtonElement}
            cycleIdToTaggedPracticesCountMapping={
              cycleIdToTaggedPracticesCountMapping
            }
            t={t}
            testId={`${testId}-tagSnp`}
            showDesignV2={showDesignV2}
            isGlobalHeaderOnMultipleScreensEnabled={
              isGlobalHeaderOnMultipleScreensEnabled
            }
          />
        );
      }
      case "STANDARD_LIST": {
        return (
          <TagPractices
            itemType={itemType}
            itemTitle={itemTitle}
            setId={cycleIdToSetIdMapping[currentCycle] ?? parentSetId}
            goToPreviousPage={goToPreviousPage}
            cycleDetails={{ cycleName, cycleType }}
            thumbnailData={thumbnailData}
            closeButtonElement={closeButtonElement}
            handleTaggedPractices={handleTaggedPractices}
            practiceListsCollection={practiceListsCollection}
            showBackButton={_.size(cycles) > 1}
            t={t}
            showDesignV2={showDesignV2}
            closeModal={closeModal}
            testId={`${testId}-tagSnp`}
            isGlobalHeaderOnMultipleScreensEnabled={
              isGlobalHeaderOnMultipleScreensEnabled
            }
          />
        );
      }
    }
  };

  return (
    <div className={classes.container}>
      {getCurrentScreen()}

      {isLoading ? <FullScreenLoader /> : null}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const organizationId = userInfo.org_id;
  const curriculumProgramId = ownProps.curriculumProgramId;
  const userId = userInfo.id;

  const variables = {
    id: organizationId,
    filters: {
      isArchived: false,
      curriculumProgramId,
    },
  };

  const canManageEvaluationCycles = ACLStorage.getPermissionValue(
    SNPv2_MANAGE_EVALUATION_CYCLES_PERMISSION
  );

  const isVisitor = ACLStore.can(SNPv2_VISITOR_PORTAL_PERM_GROUP);

  const isGlobalHeaderOnMultipleScreensEnabled = ACLStore.can(
    ENABLE_GLOBAL_HEADER_ON_MULTIPLE_SCREENS
  );

  return {
    userId,
    variables,
    isData: true,
    isLoading: false,
    isGlobalHeaderOnMultipleScreensEnabled,
    canManageEvaluationCycles,
    isVisitor,
  };
};

const mapActionCreators = {
  tagPractices,
};

export default compose(
  I18nHOC({ resources: ["snpTemplate", "snp", "common"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getSnPEvaluationCyclesQuery, {
    name: "getSnPEvaluationCyclesQuery",
    alias: "getSnPEvaluationCyclesQuery",
    options: ({ variables }) => {
      return {
        variables,
        fetchPolicy: "cache-and-network",
      };
    },
    props: ({ getSnPEvaluationCyclesQuery }) => {
      const { networkStatus, variables } = getSnPEvaluationCyclesQuery;

      const data = getSnPEvaluationCyclesFromCache(variables);
      const newSnpCycles = getSnpV2Cycles({
        cycles: _.get(data, "snpEvaluationCycles", []),
      });

      return {
        isData: !_.isEmpty(data),
        isLoading: _.includes(
          GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
          networkStatus
        ),
        cycles: newSnpCycles,
      };
    },
  }),
  withLoader
)(TagSnp);

TagSnp.defaultProps = {
  taggedPractices: [],
  showApprovedPractices: true,
  handleTaggingExternally: false,
  parentCycleId: "",
  parentSetId: "",
  itemGroupType: "",
  steps: ["CYCLE_LIST", "STANDARD_LIST"],
  testId: "appComponents-tagSnpModalV2",
};

TagSnp.propTypes = {
  itemGroupType: PropTypes.string,
  showApprovedPractices: PropTypes.bool,
  testId: PropTypes.string,
};
